// Import Ionic Components
import {
  IonButton,
  IonCard,
  IonCol,
  IonGrid,
  IonLoading,
  IonRow,
  useIonAlert,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from "@ionic/react";

// Import React Libraries
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

// Import Styles
import classNames from "classnames/bind";
import styles from "./cart.module.scss";
import "./cart.scss";

// Import Components
import IsEmptyCart from "../../components/Cart/IsEmptyCart/IsEmptyCart";
import TotalPrice from "../../components/Cart/TotalPrice/TotalPrice";
import ContinueButton from "../../components/ContinueButton/ContinueButton";
import CouponUnited from "../../components/Cart/Coupon/CouponUnited";
import MaxWidthContainer from "../../components/UI/MaxWidthContainer/MaxWidthContainer";
import PageTitle from "../../components/PageTitle/PageTitle";
import { BonusPoints } from "../../components/Cart/BonusPoints/BonusPoints";
import { useConfig } from "../../providers/config";
import { useAuth } from "../../contexts/auth-context";
import { ShippingMethod, Product as TProduct } from "@core/types";
import { useCartData } from "../../providers/cartDataStore";
import { useGetIncludeProductsQuery } from "../../providers/products";
import { CartItemSkeleton } from "../../components/UI/CartItemSkeleton/CartItemSkeleton";
import { useGetCouponMutation } from "../../providers/coupons";
import { useGetShippingMethodsQuery } from "../../providers/shippingAndPayment";
import { useActiveOrMainShippingZone } from "../../providers/shippingZones";
import { getLowerValueObj } from "../../utils";
import { AppMetricaProvider } from "../../providers/AppMetrica/AppMetrica";
import { useMarketMode } from "../../providers/marketMode";
import Page from "../page-layout";
import UnavailableProductCard from "../../core/src/entities/Product/CartUnavailableProduct";
import { CartProductCard } from "@core/components";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";

const cx = classNames.bind(styles);

function getExpressDeliveryText(
  t: TFunction,
  isMinimumOrderNotMet: boolean,
  isWeightWithinExpressLimit: boolean,
  minimumOrderAmount: number,
  expressMaxWeightKg: number,
) {
  if (isMinimumOrderNotMet && !isWeightWithinExpressLimit) {
    return `Не менее ${minimumOrderAmount!} рублей и вес корзины не должен превышать ${expressMaxWeightKg}кг`;
  } else if (!isMinimumOrderNotMet && !isWeightWithinExpressLimit) {
    return `${t("Cart weight limit exceeded")} ${expressMaxWeightKg}кг`;
  } else if (isMinimumOrderNotMet && isWeightWithinExpressLimit) {
    return `Не менее ${minimumOrderAmount!} рублей`;
  } else {
    return t("Proceed");
  }
}

function getMarketDeliveryText(t: TFunction, isMinimumOrderNotMet: boolean, minimumOrderAmount: number) {
  return isMinimumOrderNotMet ? `Не менее ${minimumOrderAmount} рублей` : t("Proceed");
}

const Cart: React.FC = () => {
  const { t } = useTranslation();
  const { user, isUserAuthenticated } = useAuth();
  const {
    cartProducts,
    cartTotalValue,
    cartSubtotalValue,
    replaceCartProducts,
    applyCoupon,
    getProductsTotalWeight,
    resetCart,
    unavailableProducts,
    soldOutProducts,
    clearSoldOutProducts,
    clearUnavailableProducts,
    removeSoldOutProduct,
    removeUnavailableProduct,
  } = useCartData();

  const [presentAlert] = useIonAlert();
  const history = useHistory();

  const [shouldCheckProducts, setShouldCheckProducts] = useState(false);

  const config = useConfig();
  const [minimumOrderAmount, setMinimumOrderAmount] = useState(config?.minimumOrderAmount);

  const { activeShippingZone } = useActiveOrMainShippingZone();

  const activeShippingZoneId = activeShippingZone.id;

  const { data: products, isFetching: isIncludeProductsLoading } = useGetIncludeProductsQuery({
    include: cartProducts.map((product) => String(product.id)),
    params: {
      zoneId: activeShippingZoneId,
    },
    options: {
      enabled: shouldCheckProducts && !!activeShippingZoneId,
    },
  });
  const { mutate: getCouponMutation, isLoading: isCouponLoading } = useGetCouponMutation();

  const { mode } = useMarketMode();
  const [cartProductsWeightKg, setCartProductsWeightKg] = useState(0);

  const modeExpressEnabledAndAllowed = config?.isExpressDeliveryEnabled && mode === "express";
  const isWeightWithinExpressLimit = modeExpressEnabledAndAllowed && cartProductsWeightKg <= config.expressMaxWeightKg;

  const isMinimumOrderNotMet = cartSubtotalValue < minimumOrderAmount!;

  const continueButtonDisabled = modeExpressEnabledAndAllowed
    ? isMinimumOrderNotMet || !isWeightWithinExpressLimit
    : isMinimumOrderNotMet;

  const calculateCartWeightKg = useCallback(() => {
    const productsWeight = cartProducts.reduce((acc, cartProduct) => {
      const cartProductWeight = +cartProduct.weight * cartProduct.quantity;
      acc += cartProductWeight;
      return acc;
    }, 0);

    setCartProductsWeightKg(productsWeight);
  }, [cartProducts]);

  useEffect(() => {
    if (cartProducts) {
      calculateCartWeightKg();
    }
  }, [calculateCartWeightKg, cartProducts]);

  useIonViewWillEnter(() => {
    if (cartProducts) {
      calculateCartWeightKg();
      setShouldCheckProducts(true);
    }
  }, [calculateCartWeightKg, cartProducts]);

  useIonViewWillLeave(() => {
    setShouldCheckProducts(false);
  });

  const buttonText = modeExpressEnabledAndAllowed
    ? getExpressDeliveryText(
        t,
        isMinimumOrderNotMet,
        isWeightWithinExpressLimit!,
        minimumOrderAmount!,
        config?.expressMaxWeightKg,
      )
    : getMarketDeliveryText(t, isMinimumOrderNotMet, minimumOrderAmount!);

  const shouldEnableShippingQuery =
    config?.cartMinAmountFromMethods && (config.shipping.isShippingZonesEnabled || !!activeShippingZoneId);

  const { data: shippingMethods } = useGetShippingMethodsQuery(activeShippingZoneId, getProductsTotalWeight(), {
    enabled: shouldEnableShippingQuery,
  });

  const productsMap = useMemo(() => {
    if (!products?.length) return {};

    return products.reduce(
      (mapObj, product) => {
        mapObj[product.id] = product;
        return mapObj;
      },
      {} as Record<string, TProduct>,
    );
  }, [products]);

  useEffect(() => {
    const lowerValueObj = getLowerValueObj<ShippingMethod, "minAmount">(shippingMethods || [], "minAmount");

    const minimumOrderAmount = lowerValueObj?.minAmount ?? config.minimumOrderAmount;

    setMinimumOrderAmount(Number(minimumOrderAmount));
  }, [shippingMethods]);

  useEffect(() => {
    if (products) {
      replaceCartProducts(products);
      setShouldCheckProducts(false);
    }
  }, [products]);

  const showInvalidCouponAlert = () => {
    presentAlert({
      header: t("Alert"),
      message: t("Invalid Coupon Code!"),
      buttons: [t("Ok")],
    });
  };

  const getCoupon = async (code: string) => {
    if (code) {
      getCouponMutation(code, {
        onSuccess: async (data) => {
          if (data) {
            applyCoupon(data);
          } else {
            showInvalidCouponAlert();
          }
        },
      });
    }
  };

  const proceedToCheckOut = async () => {
    AppMetricaProvider.reportEvent("cartData", { data: cartProducts });
    history.push("/shipping-address");
  };

  const openProductsPage = () => {
    history.push("/search-main-categories");
  };

  const isCartEmpty = config.isUnavailableProductsEnabled
    ? !cartSubtotalValue && !soldOutProducts?.length && !unavailableProducts?.length
    : !cartSubtotalValue;

  const handleResetCart = () => {
    resetCart(isUserAuthenticated);
    if (config.isUnavailableProductsEnabled) {
      clearSoldOutProducts(isUserAuthenticated);
      clearUnavailableProducts(isUserAuthenticated);
    }
  };

  const isProductsLoadedAndCartIsEmpty = products?.length !== 0 && !!config;

  return (
    <Page>
      <Page.HeaderMobile showSearchButton />

      <Page.Content>
        <Page.HeaderDesktop breadcrumbs={[{ title: t("Cart Page") }]} />
        {/* вывод сообщения когда введен купон  */}
        <IonLoading isOpen={isIncludeProductsLoading} message={t("Please wait...")} duration={20000} />
        <div
          className={`global-content ${cx({
            cartContentEmpty: isCartEmpty,
          })}`}
        >
          {isCartEmpty ? (
            <IsEmptyCart openProductsPage={() => openProductsPage()} />
          ) : (
            <IonGrid className={cx("cartGrid", "lg")}>
              <MaxWidthContainer smContainer={true} mdContainer={true} lgContainer={true}>
                <PageTitle hideTitleBreakpoint={"ion-hide-md-down"} title={t("Cart Page")} />
                <IonRow className={cx("cartWrapper")}>
                  {/* карточки товаров */}
                  <IonCol sizeLg={"8"} className={cx("cartProducts")}>
                    <IonRow className={cx("cartActions")}>
                      <IonButton fill="clear" size="small" onClick={handleResetCart}>
                        {t("Clear")}
                      </IonButton>
                    </IonRow>

                    {config.isUnavailableProductsEnabled && !cartProducts.length && !!unavailableProducts?.length && (
                      <IonRow className={cx("cartActions")}>
                        <IonButton
                          fill="clear"
                          size="small"
                          onClick={() => clearUnavailableProducts(isUserAuthenticated)}
                        >
                          {t("Reset unavailable products")}
                        </IonButton>
                      </IonRow>
                    )}

                    {config.isUnavailableProductsEnabled && !cartProducts.length && !!soldOutProducts?.length && (
                      <IonRow className={cx("cartActions")}>
                        <IonButton fill="clear" size="small" onClick={() => clearSoldOutProducts(isUserAuthenticated)}>
                          {t("Reset sold out products")}
                        </IonButton>
                      </IonRow>
                    )}

                    {isIncludeProductsLoading
                      ? cartProducts?.map((cartItem) => {
                          return <CartItemSkeleton key={cartItem.id} />;
                        })
                      : cartProducts?.map((cartItem) => {
                          const product = productsMap[cartItem.id];
                          return product ? (
                            <CartProductCard
                              className="first-of-type:mt-1"
                              cartItem={cartItem}
                              product={product}
                              key={cartItem.id}
                            />
                          ) : null;
                        })}

                    {config.isUnavailableProductsEnabled &&
                      (isIncludeProductsLoading
                        ? soldOutProducts?.map((soldOutProduct) => {
                            return <CartItemSkeleton key={soldOutProduct.id} />;
                          })
                        : soldOutProducts?.map((soldOutProduct) => {
                            // * Show only completely sold out products.
                            // * Partially sold out products (there were 6 bananas, now there are 5). We show in the cards that are in the basket.
                            if (soldOutProduct.stockQuantity >= 1) return null;
                            const product = productsMap[soldOutProduct.parentId];

                            return soldOutProduct ? (
                              <UnavailableProductCard
                                product={product}
                                key={soldOutProduct.id}
                                cartItem={soldOutProduct}
                                removeUnavailableProduct={removeSoldOutProduct}
                                removeProductCardTxt={t("Sold out")}
                                className="first-of-type:mt-1 opacity-60"
                              />
                            ) : null;
                          }))}

                    {config.isUnavailableProductsEnabled &&
                      (isIncludeProductsLoading
                        ? unavailableProducts?.map((unavailableProduct) => {
                            return <CartItemSkeleton key={unavailableProduct.id} />;
                          })
                        : unavailableProducts?.map((unavailableProduct) => {
                            const product = productsMap[unavailableProduct.parentId];

                            return unavailableProduct ? (
                              <UnavailableProductCard
                                product={product}
                                key={unavailableProduct.id}
                                cartItem={unavailableProduct}
                                className="first-of-type:mt-1 opacity-60"
                                removeUnavailableProduct={removeUnavailableProduct}
                                removeProductCardTxtClassName="text-xs md:text-base"
                                removeProductCardTxt={t("Not available in your delivery area")}
                              />
                            ) : null;
                          }))}
                  </IonCol>
                  {/*<MapComponent />*/}
                  <IonCol sizeLg={"4"} className={cx("cartTotalInfo")}>
                    {/* Блок купонов */}
                    {products?.length !== 0 && config?.showCouponsInCart && (
                      <CouponUnited getCoupon={getCoupon} isCouponLoading={isCouponLoading} />
                    )}
                    {!!user && config?.isBonusPointsEnabled && <BonusPoints />}
                    {/* итоговая цена */}
                    {products?.length !== 0 && (
                      <TotalPrice
                        discount={cartSubtotalValue - cartTotalValue}
                        subtotal={cartSubtotalValue}
                        total={cartTotalValue}
                      />
                    )}
                    {/*  cart continue button */}
                    {isProductsLoadedAndCartIsEmpty && (
                      <IonCard className={cx("ion-hide-md-down")}>
                        <ContinueButton
                          onClick={() => proceedToCheckOut()}
                          disabled={continueButtonDisabled}
                          buttonText={buttonText}
                        />
                      </IonCard>
                    )}
                  </IonCol>
                </IonRow>
              </MaxWidthContainer>
            </IonGrid>
          )}
        </div>
        <Page.ContentFooter />
      </Page.Content>

      <Page.PageFooter className={cx("oneButtonFooter")}>
        <div className={cx("ion-hide-md-up")}>
          {isProductsLoadedAndCartIsEmpty && (
            <ContinueButton
              onClick={() => proceedToCheckOut()}
              disabled={continueButtonDisabled}
              buttonText={buttonText}
            />
          )}
        </div>
      </Page.PageFooter>
    </Page>
  );
};

export default Cart;
